<template>
  <form>
    <div class="px-6">
      <div class="mb-6 flex flex-row" v-if="isEditMode && isHead">
        <div class="w-1/3 mr-6">
          <BaseLabel for-id="status">Статус</BaseLabel>
          <LazySelect
            id="status"
            label="name"
            v-model="formData.status"
            :source="loadRequestStatusesSelect"
            :disabled="isDisabled('status')"
          />
        </div>
        <div class="w-1/3" v-if="formData.newStatus">
          <BaseLabel for-id="new_status">Требует подтверждения</BaseLabel>
          <BaseSelect
            id="new_status"
            label="name"
            v-model="formData.newStatus"
            :disabled="isDisabled('new_status')"
          />
        </div>
      </div>

      <div class="mb-6 flex flex-col rounded bg-cultured px-3 py-3">
        <div
          class="flex flex-row justify-between"
          :class="{'mb-3': !formData.status}"
        >
          <div class="font-bold">Данные ЖСт</div>
          <div v-if="showStatus">
            <LMSPStatus :status="formData.status" />
          </div>
        </div>
        <div class="flex flex-row">
          <div class="w-1/3 mr-6">
            <BaseLabel for-id="life_situation">Жизненная ситуация</BaseLabel>
            <LazySelect
              id="life_situation"
              label="name"
              v-model="formData.lifeSituation"
              :source="loadLifeSituationSelect"
              @input="onChangeLifeSituation"
              :disabled="isDisabled('life_situation')"
              :hasError="hasFormError('life_situation')"
            />
            <BaseError v-if="hasFormError('life_situation')">
              {{ getFormError('life_situation') }}
            </BaseError>
          </div>
          <div class="w-2/3" v-if="showComment">
            <BaseLabel for-id="register">
              Комментарий Уполномоченного органа
            </BaseLabel>
            <p>{{ formData.comment }}</p>
          </div>
        </div>
      </div>

      <div class="mb-2 mt-6 font-bold">Данные меры</div>
      <div class="mb-2 flex flex-row">
        <div class="w-1/2 mr-6">
          <BaseLabel for-id="uuid">
            Идентификатор ЛМСЗ в ЕГИССО (UUID)
          </BaseLabel>
          <div class="flex flex-col w-full">
            <div class="flex flex-row w-full">
              <div class="w-full mr-3">
                <BaseInput
                  v-model="formData.uuid"
                  id="uuid"
                  :disabled="isDisabled('uuid')"
                  :hasError="hasFormError('uuid')"
                />
              </div>
              <BaseButton
                size="xs"
                variant="blue-transparent"
                :disabled="isDisabled('uuid') || isLoading"
                @click="findLMSP"
              >
                <BaseLoader v-if="isLoading" size="xs" class="mr-1" />
                Найти
              </BaseButton>
            </div>
            <BaseError v-if="hasFormError('uuid')">
              {{ getFormError('uuid') }}
            </BaseError>
          </div>
        </div>
      </div>
      <div class="mb-2 flex flex-row">
        <div class="w-1/2 mr-6">
          <BaseLabel for-id="frgu_code">
            Реестровый номер услуги (системный код услуги)
          </BaseLabel>
          <BaseInput
            v-model="formData.frguCode"
            id="frgu_code"
            :hasError="hasFormError('frgu_code')"
            :disabled="isDisabled('frgu_code')"
          />
          <BaseError v-if="hasFormError('frgu_code')">
            {{ getFormError('frgu_code') }}
          </BaseError>
        </div>
        <div class="mb-2 w-1/2">
          <BaseLabel for-id="target_id">Идентификатор цели</BaseLabel>
          <BaseInput
            v-model="formData.targetId"
            id="target_id"
            :hasError="hasFormError('target_id')"
            :disabled="isDisabled('target_id')"
          />
          <BaseError v-if="hasFormError('target_id')">
            {{ getFormError('target_id') }}
          </BaseError>
        </div>
      </div>
      <div class="mb-2">
        <BaseLabel for-id="lmsp_name">Наименование ЛМСЗ</BaseLabel>
        <BaseInput
          v-model="formData.lmspName"
          id="lmsp_name"
          :disabled="isDisabled('lmsp_name')"
        />
      </div>
      <div class="mb-6 flex flex-row">
        <div class="mr-6 w-1/3">
          <BaseLabel for-id="lmsp_code">Код ЛМСЗ</BaseLabel>
          <BaseInput
            v-model="formData.lmspCode"
            id="lmsp_code"
            :disabled="isDisabled('lmsp_code')"
          />
        </div>
        <div class="w-full">
          <BaseLabel for-id="npa_level">
            Уровень нормативно-правового регулирования
          </BaseLabel>
          <BaseInput
            :value="fullNPA"
            id="npa_level"
            :disabled="isDisabled('npa_level')"
          />
        </div>
      </div>

      <div class="mb-2 font-bold">Нормативно-правовое регулирование</div>
      <div
        class="mb-6"
        v-for="(legalAct, index) in formData.legalActs"
        :key="index"
      >
        <BaseLabel for-id="legal_act_name">
          НПА (устанавливающий МСЗ и/или регламентирующий условия определения
          права на МСЗ)
        </BaseLabel>
        <BaseInput
          v-model="legalAct.name"
          id="legal_act_name"
          class="mb-2"
          :disabled="isDisabled('legal_acts')"
        />
      </div>

      <div class="mb-2 font-bold">Классификация меры</div>
      <div class="mb-4">
        <BaseLabel for-id="section">Раздел КМСЗ</BaseLabel>
        <BaseSelect
          id="section"
          label="name"
          v-model="formData.section"
          :disabled="isDisabled('section')"
        />
      </div>
      <div class="mb-6 flex flex-row">
        <div class="w-1/2 mr-6">
          <BaseLabel for-id="registry_code">Код меры КМСЗ</BaseLabel>
          <BaseInput
            id="registry_code"
            :value="formData.register && formData.register.code"
            :disabled="isDisabled('registry_code')"
          />
        </div>
        <div class="w-1/2">
          <BaseLabel for-id="register">Мера КМСЗ</BaseLabel>
          <BaseSelect
            id="register"
            label="name"
            v-model="formData.register"
            :disabled="isDisabled('register')"
          />
        </div>
      </div>

      <div class="mb-6">
        <BaseCondition
          :conditions="conditions"
          :errors="formError"
          v-model="formData.baseConditions"
          :disabled="isDisabled('base_conditions')"
        />
      </div>

      <div class="mb-6">
        <BaseCheckbox
          label="Уточнить варианты условий для меры"
          v-model="formData.includeVariants"
          :disabled="isDisabled('include_variants')"
        />
      </div>

      <CategoryCondition
        v-model="formData.categories"
        :show-add-req-btn="showAddCategoryConditionReqBnt"
        :conditions="conditions"
        :base-conditions="formData.baseConditions"
        :closed="!this.formData.includeVariants"
        :disabled="isDisabled('categories')"
        :errors="formError"
      >
        <template #empty-category>Добавьте Идентификатор ЛМСЗ</template>
      </CategoryCondition>
    </div>

    <div v-if="showUserComment">
      <div class="px-6">
        <div class="mt-6">
          <BaseLabel for-id="user_comment">
            Пользовательский комментарий
          </BaseLabel>
          <BaseTextarea
            id="user_comment"
            v-model="formData.userComment"
            :disabled="isDisabled('user_comment')"
          />
        </div>
        <div class="mt-6">
          <BaseLabel for-id="comment">Комментарий</BaseLabel>
          <BaseTextarea
            id="comment"
            v-model="formData.comment"
            :disabled="isDisabled('comment')"
          />
        </div>
      </div>

      <Divider class="my-6" />

      <div class="px-6">
        <div class="mb-2 mt-6 font-bold">Комментарии</div>
        <CommentList :source="formData.id" type="lmsp_req" />
      </div>
    </div>

    <Divider class="my-6" />

    <div
      class="my-6 px-6 flex flex-row justify-between"
      v-if="showUserBtsOnCreateMode"
    >
      <div>
        <BaseButton
          class="mr-6"
          size="sm"
          variant="blue"
          :disabled="isLoading"
          @click="save('edited')"
        >
          <BaseLoader v-if="isLoading" size="xs" class="mr-1" />
          Сохранить изменения
        </BaseButton>
      </div>
      <div>
        <BaseButton
          class="mr-6"
          size="sm"
          variant="blue-transparent"
          :disabled="isLoading"
          @click="saveConfirm()"
        >
          <BaseLoader v-if="isLoading" size="xs" class="mr-1" />
          <span v-if="isIndependentUser">Включить в ЖСт</span>
          <span v-else>Включить в ЛМСЗ и отправить на согласование</span>
        </BaseButton>
      </div>
    </div>

    <div
      class="my-6 px-6 flex flex-row justify-between"
      v-if="showUserBtsOnEditMode"
    >
      <div>
        <BaseButton
          v-if="showEditBtn"
          class="mr-6"
          size="sm"
          variant="blue"
          :disabled="isLoading"
          @click="save('edited')"
        >
          <BaseLoader v-if="isLoading" size="xs" class="mr-1" />
          <span v-if="onFinalStatus || onApprovalStatus">
            Вернуть на редактирование
          </span>
          <span v-else>Сохранить изменения</span>
        </BaseButton>
      </div>
      <div>
        <BaseButton
          v-if="showIncludeBtn"
          class="mr-6"
          size="sm"
          variant="blue-transparent"
          :disabled="isLoading"
          @click="saveConfirm()"
        >
          <BaseLoader v-if="isLoading" size="xs" class="mr-1" />
          <span v-if="isIndependentUser">Включить в ЖСт</span>
          <span v-else-if="isUser">
            Включить в ЛМСЗ и отправить на согласование
          </span>
        </BaseButton>
        <BaseButton
          v-if="showExcludeBtn"
          size="sm"
          variant="blue-transparent"
          :disabled="isLoading"
          @click="showExcludePopup"
        >
          <BaseLoader v-if="isLoading" size="xs" class="mr-1" />
          <span v-if="isIndependentUser">Исключить из ЖСт</span>
          <span v-if="isUser">
            Исключить из ЖСт и отправить на согласование
          </span>
        </BaseButton>
      </div>
    </div>

    <div class="my-6 px-6" v-if="showOperatorBts">
      <BaseButton
        class="mr-6"
        size="sm"
        variant="blue"
        :disabled="isLoading"
        @click="save('edited')"
      >
        <BaseLoader v-if="isLoading" size="xs" class="mr-1" />
        Сохранить изменения
      </BaseButton>
    </div>

    <LMSPExcludePopup
      :show="excludePopup.visible"
      @save="saveExclude"
      @close="hideExcludePopup"
    />

    <LMSPConfirmPopup
      :show="confirmPopup.visible"
      @save="onConfirmPopup"
      @close="hideConfirmPopup"
    />
  </form>
</template>

<script>
  import {mapState, mapGetters} from 'vuex';

  import cloneDeep from 'lodash/cloneDeep';
  import Client from '@/classes/Api/Client';
  import {LocalStore} from '@/utils/storage';
  import WithHttpError from '@/components/mixins/WithHttpError';

  import Divider from '@/components/global/base-divider';
  import BaseLabel from '@/components/global/base-label';
  import BaseError from '@/components/global/base-error';
  import BaseInput from '@/components/global/base-input';
  import LMSPStatus from '@/components/views/lmsp/status';
  import LazySelect from '@/components/global/lazy-select';
  import BaseSelect from '@/components/global/base-select';
  import BaseButton from '@/components/global/base-button';
  import BaseLoader from '@/components/global/base-loader';
  import CommentList from '@/components/global/comment-list';
  import BaseTextarea from '@/components/global/base-textarea';
  import BaseCheckbox from '@/components/global/base-checkbox';
  import BaseCondition from '@/components/global/base-condition';
  import CategoryCondition from '@/components/global/category-condition';
  import LMSPExcludePopup from '@/components/global/popups/LMSPExcludePopup';
  import LMSPConfirmPopup from '@/components/global/popups/LMSPConfirmPopup';

  export default {
    name: 'LMSPForm',

    components: {
      Divider,
      BaseLabel,
      BaseError,
      BaseInput,
      LMSPStatus,
      LazySelect,
      BaseSelect,
      BaseButton,
      BaseLoader,
      CommentList,
      BaseTextarea,
      BaseCheckbox,
      BaseCondition,
      LMSPExcludePopup,
      LMSPConfirmPopup,
      CategoryCondition,
    },

    mixins: [WithHttpError],

    props: {
      data: {
        type: Object,
        default: () => ({}),
      },
    },

    data() {
      return {
        formData: {
          id: null,
          lifeSituation: null,
          legalActs: [{name: null}],
          npaLevel: {id: null, name: null, code: null},

          isNew: false,

          status: null,
          newStatus: null,

          uuid: null,
          frguCode: null,
          targetId: null,
          lmspCode: null,
          lmspName: null,

          section: null,
          register: null,

          categories: [],
          baseConditions: [],
          includeVariants: false,

          comment: null,
          userComment: null,
        },

        disabledFields: [],

        isLoading: false,

        excludePopup: {
          visible: false,
        },

        confirmPopup: {
          visible: false,
        },
      };
    },

    computed: {
      ...mapGetters('Auth', ['hasRole']),
      ...mapState('Dictionary', ['conditions']),

      isHead() {
        return this.hasRole('head') || this.hasRole('operator');
      },

      isHeadPlus() {
        return this.hasRole('head_plus');
      },

      isUser() {
        return this.hasRole('user');
      },

      isUserPlus() {
        return this.hasRole('user_plus');
      },

      isIndependentUser() {
        return this.isUserPlus || this.isHeadPlus;
      },

      isEditMode() {
        return this.formData.id !== null;
      },

      isNewRequest() {
        return this.formData.id === null;
      },

      onApprovalStatus() {
        return (
          this.formData.status && this.formData.status.slug === 'on_approval'
        );
      },

      onExcludedStatus() {
        return this.formData.status && this.formData.status.slug === 'excluded';
      },

      onEditedStatus() {
        return this.formData.status && this.formData.status.slug === 'edited';
      },

      onConfirmedStatus() {
        return (
          this.formData.status && this.formData.status.slug === 'confirmed'
        );
      },

      onFinalStatus() {
        return this.onConfirmedStatus || this.onExcludedStatus;
      },

      formDisabled() {
        if (this.isHead) {
          return this.onEditedStatus;
        }
        if (this.isIndependentUser) {
          return false;
        }
        return this.onApprovalStatus || this.onExcludedStatus;
      },

      showStatus() {
        return this.formData.status && (this.isUser || this.isIndependentUser);
      },

      showComment() {
        return this.formData.comment && this.isUser;
      },

      showUserComment() {
        return this.isHead;
      },

      showAddCategoryConditionReqBnt() {
        return this.isUser || this.isIndependentUser;
      },

      showUserBtsOnCreateMode() {
        return this.isNewRequest && (this.isUser || this.isIndependentUser);
      },

      showUserBtsOnEditMode() {
        return (
          this.isEditMode &&
          !this.formDisabled &&
          (this.isUser || this.isIndependentUser)
        );
      },

      showEditBtn() {
        if (this.isUser) {
          return this.onEditedStatus || this.onConfirmedStatus;
        }
        return true;
      },

      showIncludeBtn() {
        return !this.onFinalStatus;
      },

      showExcludeBtn() {
        return !this.onFinalStatus;
      },

      showOperatorBts() {
        return this.isHead && !this.formDisabled;
      },

      lifeSituationId() {
        if (!this.formData.lifeSituation) {
          return null;
        }

        return this.formData.lifeSituation.id;
      },

      fullNPA() {
        let code = this.formData.npaLevel.code;
        let name = this.formData.npaLevel.name;
        if (!code || !name) {
          return '';
        }
        return `${code} ${name}`;
      },
    },

    created() {
      this.bindFormData();
    },

    destroyed() {
      this.resetForm();
      this.clearDictionary();
    },

    watch: {
      data() {
        this.bindFormData();
      },
    },

    methods: {
      showExcludePopup() {
        this.excludePopup.visible = true;
      },

      hideExcludePopup() {
        this.excludePopup.visible = false;
      },

      showConfirmPopup() {
        this.confirmPopup.visible = true;
      },

      hideConfirmPopup() {
        this.confirmPopup.visible = false;
      },

      onConfirmPopup(params) {
        if (params.neverShow) {
          LocalStore.put('show_confirm', 0);
        }
        this.hideConfirmPopup();
        this.save('confirmed');
      },

      loadLifeSituationSelect() {
        return Client.getLifeSituations().then((response) => {
          return response.data;
        });
      },

      loadRequestStatusesSelect() {
        return Client.getLMSPReqStatuses().then((response) => {
          return response.data;
        });
      },

      onChangeLifeSituation(val) {
        Object.assign(this, {
          formData: Object.assign(this.formData, {
            lifeSituation: val,
            baseConditions: [],
            categories: this.formData.categories.map((item) => {
              item.values = [];
              return item;
            }),
          }),
        });

        this.loadConditions();
      },

      isDisabled(field) {
        if (this.formDisabled) {
          return true;
        }
        return this.disabledFields.indexOf(field) !== -1;
      },

      setDisabledFields() {
        this.disabledFields = this.isHead
          ? this.getDisabledFieldsForHead()
          : this.getDisabledFieldsForUser();
      },

      getDisabledFieldsForUser() {
        let disabledFields = [
          'status',
          'new_status',
          'lmsp_code',
          'lmsp_name',
          'npa_level',
          'section',
          'register',
          'registry_code',
          'legal_acts',
          'comment',
        ];

        if (this.isEditMode) {
          disabledFields = disabledFields.concat(['uuid', 'life_situation']);
        }

        if (this.onFinalStatus) {
          disabledFields = disabledFields.concat([
            'frgu_code',
            'target_id',
            'categories',
            'base_conditions',
            'include_variants',
          ]);
        }

        return disabledFields;
      },

      getDisabledFieldsForHead() {
        return [
          'new_status',
          'life_situation',
          'uuid',
          'frgu_code',
          'target_id',
          'lmsp_code',
          'lmsp_name',
          'npa_level',
          'section',
          'register',
          'registry_code',
          'legal_acts',
          'categories',
          'base_conditions',
          'include_variants',
          'user_comment',
        ];
      },

      findLMSP() {
        if (!(!this.isLoading && (this.isLoading = true))) {
          return;
        }

        return Client.getLMSPInfo(this.formData.uuid)
          .then((response) => {
            this.clearFormErrors();

            let data = response.data;
            this.formData = Object.assign(this.formData, {
              lmspCode: data.lmsp_code,
              lmspName: data.lmsp_name,
              npaLevel: data.npa_level,
              section: data.section || {},
              register: data.register || {},
              legalActs: data.legal_acts || {},
              categories: data.categories.map((item) => {
                item.values = [];
                return item;
              }),
            });
          })
          .catch(this.prepareHttpError)
          .finally(() => {
            this.isLoading = false;
          });
      },

      bindFormData() {
        for (let i in this.data.categories || []) {
          let category = this.data.categories[i];
          this.data.categories[i].values = this.data.conditions.filter(
            (item) => item.category_id === category.id
          );
        }

        this.formData = {
          id: this.data.id || null,
          lifeSituation: this.data.life_situation || null,
          legalActs: this.data.legal_acts || [{name: null}],
          npaLevel: this.data.npa_level || {id: null, name: null, code: null},

          isNew: this.data.is_new || null,

          status: this.data.status || null,
          newStatus: this.data.new_status || null,

          uuid: this.data.lmsp_uid || null,
          frguCode: this.data.frgu_code || null,
          targetId: this.data.target_id || null,
          lmspCode: this.data.lmsp_code || null,
          lmspName: this.data.lmsp_name || null,

          section: this.data.section || null,
          register: this.data.register || null,

          categories: this.data.categories || [],
          baseConditions: this.data.base_conditions || [],
          includeVariants: this.data.include_variants || false,

          comment: this.data.comment || null,
          userComment: this.data.user_comment || null,
        };

        this.loadConditions();
        this.setDisabledFields();
      },

      resetForm() {
        this.formData = {
          id: null,
          lifeSituation: null,
          legalActs: [{name: null}],
          npaLevel: {id: null, name: null, code: null},

          isNew: null,

          status: null,
          newStatus: null,

          uuid: null,
          frguCode: null,
          targetId: null,
          lmspCode: null,
          lmspName: null,

          section: null,
          register: null,

          categories: [],
          baseConditions: [],
          includeVariants: false,

          comment: null,
          userComment: null,
        };

        this.clearDictionary();
      },

      saveExclude(comment) {
        this.formData.userComment = comment;
        this.save('excluded');
      },

      saveConfirm() {
        const needShow = LocalStore.get('show_confirm', 'number', 1);
        if (needShow) {
          this.showConfirmPopup();
          return;
        }
        this.save('confirmed');
      },

      save(action) {
        if (!(!this.isLoading && (this.isLoading = true))) {
          return;
        }

        let baseConditions = this.formData.baseConditions.map((x) => x.id);

        let data = {
          action: action,
          status_id: this.formData.status && this.formData.status.id,
          life_situation:
            this.formData.lifeSituation && this.formData.lifeSituation.id,
          uuid: this.formData.uuid,
          frgu_code: this.formData.frguCode,
          target_id: this.formData.targetId,
          comment: this.formData.comment,
          user_comment: this.formData.userComment,
          include_variants: this.formData.includeVariants,
          base_conditions: this.formData.baseConditions,
          categories: cloneDeep(this.formData.categories).map((item) => {
            let values = this.formData.includeVariants ? item.values : [];
            values = values.filter((x) => baseConditions.indexOf(x.id) === -1);

            return {
              id: item.id,
              conditions: values,
            };
          }),
        };

        let request = null;
        if (this.isNewRequest) {
          request = Client.saveLMSPRequest(data);
        } else {
          if (this.isHead) {
            request = Client.updateOperatorLMSPRequest(this.formData.id, data);
          } else {
            request = Client.updateLMSPRequest(this.formData.id, data);
          }
        }

        return request
          .then((response) => {
            let data = response.data;
            this.$store.commit('Notification/showAlert', {
              title: 'Изменения сохранены!',
              message: `Статус ЛМСЗ: "${data.status.name}"`,
            });

            if (!this.formData.id) {
              this.$router.push({
                name: 'registry.detail',
                params: {
                  id: data.id,
                },
              });
            } else {
              this.formData.status = data.status;
            }

            this.clearFormErrors();
            this.setDisabledFields();
          })
          .catch((err) => {
            this.prepareHttpError(err);

            this.$nextTick(() => {
              let items = document.getElementsByClassName('has-error');
              if (items.length <= 0) {
                return;
              }
              let el = items[0];
              let labelHeight = 25;
              let inputHeight = 40;
              window.scroll({
                left: 0,
                top: el.offsetTop - (inputHeight + labelHeight),
                behavior: 'smooth',
              });
            });
          })
          .catch(() => {
            this.$store.commit('Notification/showAlert', {
              title: 'Ошибка!',
              message:
                'Произошла ошибка, пожалуйста попробуйте повторно отправить заявку.',
            });
          })
          .finally(() => {
            this.isLoading = false;
          });
      },

      clearDictionary() {
        this.$store.dispatch('Dictionary/clear');
      },

      loadConditions() {
        if (this.lifeSituationId) {
          this.$store.dispatch(
            'Dictionary/loadConditions',
            this.lifeSituationId
          );
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .remove-law-btn {
    left: rem(15px);
  }
</style>
